<template>
  <div class="sku-page">
    <!-- 物品选择器 -->
    <div class="product-selector">
      <router-link :to="{ name: 'products' }" class="back-link">&larr; 返回物品列表</router-link>
      <span v-if="selectedProductId > 0" class="shop-name">所属店家：{{ selectedProductCommunityName }} - {{ selectedProductShopName }}</span>
      <label class="selector-label">选择物品：</label>
      <div class="selector-input">
        <select v-model.number="selectedProductId" class="product-select" @change="onProductChange">
          <option :value="0">请选择物品</option>
          <option v-for="p in productOptions" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>
      </div>
      <span v-if="selectedProductId > 0" class="sku-count-tip">当前 SKU 数量：{{ skus.length }}</span>
    </div>

    <!-- 筛选和操作栏 -->
    <div v-if="selectedProductId > 0" class="filter-bar">
      <div class="search-box">
        <input
          type="text"
          v-model="searchKey"
          placeholder="搜索货号..."
          class="search-input"
        />
        <button @click="search" class="search-btn">搜索</button>
      </div>
      <button @click="openSpecConfig" class="config-btn">配置商品规格</button>
      <button @click="showAddModal = true" class="add-btn">+ 添加 SKU</button>
    </div>

    <!-- SKU 列表表格 -->
    <div v-if="selectedProductId > 0" class="table-container">
      <table class="sku-table">
        <thead>
          <tr>
            <th>SKU ID</th>
            <th>图片</th>
            <th>规格</th>
            <th>货号</th>
            <th>单位</th>
            <th>售价</th>
            <th>搬运费</th>
            <th>楼层费</th>
            <th>已售</th>
            <th>已送</th>
            <th>默认</th>
            <th>状态</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredSkus.length === 0">
            <td colspan="13" class="empty-row">暂无 SKU 数据</td>
          </tr>
          <tr v-for="sku in filteredSkus" :key="sku.id">
            <td>{{ sku.id }}</td>
            <td>
              <img :src="sku.image || DEFAULT_NO_IMAGE" class="sku-img" />
            </td>
            <td>{{ skuSpecNameWithSpec(sku) }}</td>
            <td>{{ sku.code || '-' }}</td>
            <td>{{ sku.unit || '-' }}</td>
            <td>{{ formatPrice(sku.sale_price) }}</td>
            <td>{{ formatPrice(sku.carry_fee) }}</td>
            <td>{{ formatPrice(sku.floor_fee) }}</td>
            <td>{{ sku.sales_count || 0 }}</td>
            <td>{{ sku.delivery_count || 0 }}</td>
            <td>
              <span :class="'default-tag ' + (sku.is_default === 1 ? 'is-default' : '')">
                {{ sku.is_default === 1 ? '是' : '否' }}
              </span>
            </td>
            <td>
              <span :class="'status-tag status-' + sku.status">
                {{ sku.status === 1 ? '上架' : '下架' }}
              </span>
            </td>
            <td>{{ formatDate(sku.created_at) }}</td>
            <td>
              <button @click="viewDetail(sku)" class="action-btn view">详情</button>
              <button @click="editSku(sku)" class="action-btn edit">编辑</button>
              <button
                @click="toggleStatus(sku)"
                class="action-btn"
                :class="sku.status === 1 ? 'disable' : 'enable'"
              >
                {{ sku.status === 1 ? '下架' : '上架' }}
              </button>
              <button @click="deleteSkuItem(sku)" class="action-btn delete">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 空状态提示 -->
    <div v-if="selectedProductId === 0" class="empty-state">
      <p>请先选择一个物品，查看其 SKU 列表</p>
    </div>

    <!-- 添加/编辑 SKU 弹窗 -->
    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingSku ? '编辑 SKU' : '添加 SKU' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>规格（按规格维度逐项选择）</label>
            <SkuSpecSelector
              :product-id="selectedProductId"
              v-model="form.specValueIds"
              :sku-id="editingSku ? editingSku.id : null"
              :key="'ps' + productSpecVersion + (editingSku ? 'edit-' + editingSku.id : 'add')"
            />
          </div>
          <div class="form-group">
            <label>店家自定义货号</label>
            <input v-model="form.code" type="text" class="form-control" placeholder="货号 / 外部编码" />
          </div>
          <div class="form-group">
            <ImageUpload
              ref="skuImageUpload"
              :value="form.image || DEFAULT_NO_IMAGE"
              :lazy="true"
              category="product"
              label="SKU 图片（建议规格：375x375）"
              @input="val => form.image = val"
            />
          </div>
          <div class="form-group">
            <label>计量单位</label>
            <input v-model="form.unit" type="text" class="form-control" placeholder="如：件、个、斤、箱" />
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>售价（元）</label>
              <input v-model.number="salePriceYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
            <div class="form-group form-group-half">
              <label>每单位搬运费（元）</label>
              <input v-model.number="carryFeeYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>步梯每层楼层费（元）</label>
              <input v-model.number="floorFeeYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
            <div class="form-group form-group-half">
              <label>累计销量</label>
              <input v-model.number="form.sales_count" type="number" class="form-control" min="0" disabled />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>是否默认 SKU</label>
              <select v-model.number="form.is_default" class="form-control">
                <option :value="0">否</option>
                <option :value="1">是</option>
              </select>
            </div>
            <div class="form-group form-group-half">
              <label>状态</label>
              <select v-model.number="form.status" class="form-control">
                <option :value="1">上架</option>
                <option :value="0">下架</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">取消</button>
          <button @click="saveSku" class="btn-save">保存</button>
        </div>
      </div>
    </div>

    <!-- 配置商品规格 弹窗 -->
    <div v-if="showSpecConfigModal" class="modal-overlay" @click.self="closeSpecConfigModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>配置商品规格维度</h3>
          <button class="close-btn" @click="closeSpecConfigModal">×</button>
        </div>
        <div class="modal-body">
          <p class="config-tip">勾选该物品适用的规格维度，保存后可在「添加 SKU」时按这些维度选择规格值。</p>
          <div class="spec-check-list">
            <label v-for="spec in allSpecOptions" :key="spec.id" class="spec-check-item">
              <input type="checkbox" :value="spec.id" v-model="productSpecSelectedArr" />
              <span>{{ spec.name }}</span>
            </label>
            <div v-if="allSpecOptions.length === 0" class="config-empty">暂无规格维度，请先到「商品规格」中创建。</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeSpecConfigModal">取消</button>
          <button class="btn-save" @click="saveSpecConfig" :disabled="specConfigSaving">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProductList, getSkuList, createSku, updateSku, deleteSku } from '@/api/products'
import { getSpecOptions, getSpecValueOptions, getProductSpecs, createProductSpec, deleteProductSpec, getSkuSpecs, createSkuSpec, deleteSkuSpec } from '@/api/specs'
import { buildSkuName, buildSkuNameWithSpec, skuSpecListToValueIds } from '@/utils/skuSpec'
import { DEFAULT_NO_IMAGE } from '@/constants'
import SkuSpecSelector from '@/components/product/SkuSpecSelector.vue'
import ImageUpload from '@/components/common/Form/ImageUpload.vue'

export default {
  name: 'ProductSku',
  components: { SkuSpecSelector, ImageUpload },
  data() {
    return {
      selectedProductId: 0,
      searchKey: '',
      showAddModal: false,
      editingSku: null,
      productOptions: [],
      skus: [],
      specValueMap: {},
      specNameMap: {},
      showSpecConfigModal: false,
      allSpecOptions: [],
      productSpecSelectedArr: [],
      productSpecVersion: 0,
      specConfigSaving: false,
      form: {
        code: '',
        image: '',
        unit: '',
        specValueIds: [],
        sale_price: 0,
        carry_fee: 100,
        floor_fee: 10,
        sales_count: 0,
        status: 1,
        is_default: 0
      }
    }
  },
  computed: {
    DEFAULT_NO_IMAGE() {
      return DEFAULT_NO_IMAGE
    },
    salePriceYuan: {
      get() {
        return this.form.sale_price / 100
      },
      set(val) {
        this.form.sale_price = Math.round(Number(val || 0) * 100)
      }
    },
    carryFeeYuan: {
      get() {
        return this.form.carry_fee / 100
      },
      set(val) {
        this.form.carry_fee = Math.round(Number(val || 0) * 100)
      }
    },
    floorFeeYuan: {
      get() {
        return this.form.floor_fee / 100
      },
      set(val) {
        this.form.floor_fee = Math.round(Number(val || 0) * 100)
      }
    },
    filteredSkus() {
      if (!this.searchKey.trim()) {
        return this.skus
      }
      const keyword = this.searchKey.trim().toLowerCase()
      return this.skus.filter(sku => {
        return sku.code && sku.code.toLowerCase().includes(keyword)
      })
    },
    selectedProductShopName() {
      const product = this.productOptions.find(p => p.id === this.selectedProductId)
      return product?.shop?.name || '-'
    },
    selectedProductCommunityName() {
      const product = this.productOptions.find(p => p.id === this.selectedProductId)
      return product?.shop?.community?.name || '-'
    }
  },
  mounted() {
    this.loadProducts().then(() => {
      const productId = parseInt(this.$route.query.productId, 10)
      if (productId > 0) {
        this.selectedProductId = productId
        this.loadSkus()
      }
    })
  },
  methods: {
    async loadProducts() {
      try {
        let allProducts = []
        let page = 1
        let hasMore = true
        while (hasMore) {
          const response = await getProductList({ page, per_page: 100 })
          if (response && response.data) {
            const pageData = response.data
            const items = pageData.data || pageData
            if (Array.isArray(items)) {
              allProducts = allProducts.concat(items.filter(p => p && p.id !== undefined))
            }
            hasMore = page < (pageData.last_page || 1)
            page++
          } else {
            hasMore = false
          }
        }
        this.productOptions = allProducts
      } catch (error) {
      }
    },
    onProductChange() {
      this.searchKey = ''
      if (this.selectedProductId > 0) {
        this.loadSkus()
      } else {
        this.skus = []
      }
    },
    async loadSkus() {
      try {
        const response = await getSkuList(this.selectedProductId)
        if (response && response.data) {
          const data = response.data
          const items = data.data || data
          this.skus = Array.isArray(items) ? items.filter(s => s && s.id !== undefined) : []
          await this.loadSkuSpecs()
        }
      } catch (error) {
        this.skus = []
      }
    },
    // 拉取规格值映射，并为每个 SKU 加载其已选规格值用于展示名称
    async loadSkuSpecs() {
      try {
        const svRes = await getSpecValueOptions()
        const list = (svRes && svRes.data && svRes.data.spec_values) || []
        const map = {}
        list.forEach(v => { map[v.id] = v })
        this.specValueMap = map
        const spRes = await getSpecOptions()
        const specs = (spRes && spRes.data && spRes.data.specs) || []
        const specNameMap = {}
        specs.forEach(s => { if (s && s.id != null) specNameMap[s.id] = s.name })
        this.specNameMap = specNameMap
      } catch (e) {
        this.specValueMap = {}
        this.specNameMap = {}
      }
      await Promise.all(this.skus.map(async (sku) => {
        try {
          const res = await getSkuSpecs(sku.id)
          const items = (res && res.data && res.data.list) || []
          this.$set(sku, '_specValueIds', skuSpecListToValueIds(items))
        } catch (e) {
          this.$set(sku, '_specValueIds', [])
        }
      }))
    },
    skuSpecName(sku) {
      const name = buildSkuName(sku._specValueIds || [], this.specValueMap)
      return name || '-'
    },
    skuSpecNameWithSpec(sku) {
      const name = buildSkuNameWithSpec(sku._specValueIds || [], this.specValueMap, this.specNameMap)
      return name || '-'
    },
    skuLabel(sku) {
      const name = buildSkuName(sku._specValueIds || [], this.specValueMap)
      return name || `SKU#${sku.id}`
    },
    search() {
      // filteredSkus 是计算属性，已自动过滤
    },
    formatPrice(price) {
      if (price === null || price === undefined) return '-'
      return (price / 100).toFixed(2) + ' 元'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    viewDetail(sku) {
      this.$router.push({
        name: 'skuDetail',
        params: { id: sku.id },
        query: { productId: this.selectedProductId }
      })
    },
    editSku(sku) {
      this.$router.push({
        name: 'skuDetail',
        params: { id: sku.id },
        query: { productId: this.selectedProductId, edit: '1' }
      })
    },
    async toggleStatus(sku) {
      const action = sku.status === 1 ? '下架' : '上架'
      if (confirm(`确定要${action} SKU「${this.skuLabel(sku)}」吗？`)) {
        try {
          const newStatus = sku.status === 1 ? 0 : 1
          await updateSku(this.selectedProductId, sku.id, { status: newStatus })
          sku.status = newStatus
        } catch (error) {
          alert('更新状态失败')
        }
      }
    },
    async deleteSkuItem(sku) {
      if (confirm(`确定要删除 SKU「${this.skuLabel(sku)}」吗？删除后将无法恢复。`)) {
        try {
          await deleteSku(this.selectedProductId, sku.id)
          this.skus = this.skus.filter(s => s.id !== sku.id)
          alert('删除成功')
        } catch (error) {
          alert('删除失败，请稍后重试')
        }
      }
    },
    closeModal() {
      this.showAddModal = false
      this.editingSku = null
      this.form = {
        code: '',
        image: '',
        unit: '',
        specValueIds: [],
        sale_price: 0,
        carry_fee: 100,
        floor_fee: 10,
        sales_count: 0,
        status: 1,
        is_default: 0
      }
    },
    async saveSku() {
      try {
        // 懒上传：仅保存时才真正上传 SKU 图片
        const uploaded = await this.$refs.skuImageUpload.ensureUploaded()
        if (uploaded) {
          this.form.image = uploaded.url
        }
        const payload = { ...this.form }
        delete payload.specValueIds
        if (this.editingSku) {
          await updateSku(this.selectedProductId, this.editingSku.id, payload)
          await this.syncSkuSpecs(this.editingSku.id, this.form.specValueIds)
          Object.assign(this.editingSku, payload)
          alert('更新成功')
        } else {
          const response = await createSku(this.selectedProductId, payload)
          const newSku = response.data.data || response.data
          if (newSku && newSku.id !== undefined) {
            await this.syncSkuSpecs(newSku.id, this.form.specValueIds)
            this.skus.push(newSku)
          }
          alert('添加成功')
        }
        this.closeModal()
        this.loadSkus()
      } catch (error) {
        const msg = error.response?.data?.message || '保存失败，请稍后重试'
        alert(msg)
      }
    },
    // 同步 SKU 的规格值关联：先删除旧的，再按当前选择重建
    async syncSkuSpecs(skuId, valueIds) {
      try {
        const res = await getSkuSpecs(skuId)
        const list = (res && res.data && res.data.list) || []
        await Promise.all(list.map(item => deleteSkuSpec(item.id)))
      } catch (e) {
        // 忽略读取异常，继续重建
      }
      await Promise.all((valueIds || []).map(id => createSkuSpec(skuId, id)))
    },
    async openSpecConfig() {
      if (!this.selectedProductId) return
      this.showSpecConfigModal = true
      this.specConfigSaving = false
      this.productSpecSelectedArr = []
      try {
        const [optRes, psRes] = await Promise.all([
          getSpecOptions(),
          getProductSpecs(this.selectedProductId)
        ])
        this.allSpecOptions = (optRes && optRes.data && optRes.data.specs) || []
        const psList = (psRes && psRes.data && psRes.data.list) || []
        this.productSpecSelectedArr = psList.map(item => item.spec_id)
      } catch (e) {
        this.allSpecOptions = []
        this.productSpecSelectedArr = []
      }
    },
    closeSpecConfigModal() {
      this.showSpecConfigModal = false
    },
    async saveSpecConfig() {
      this.specConfigSaving = true
      try {
        const [optRes, psRes] = await Promise.all([
          getSpecOptions(),
          getProductSpecs(this.selectedProductId)
        ])
        const currentList = (psRes && psRes.data && psRes.data.list) || []
        const currentIds = currentList.map(item => item.spec_id)
        const selected = this.productSpecSelectedArr.map(Number)
        const toAdd = selected.filter(id => !currentIds.includes(id))
        const toRemove = currentList.filter(item => !selected.includes(item.spec_id))
        await Promise.all(toAdd.map(id => createProductSpec(this.selectedProductId, id)))
        await Promise.all(toRemove.map(item => deleteProductSpec(item.id)))
        this.productSpecVersion += 1
        alert('商品规格配置已保存')
        this.closeSpecConfigModal()
      } catch (e) {
        alert('保存失败，请稍后重试')
      } finally {
        this.specConfigSaving = false
      }
    }
  }
}
</script>

<style scoped>
.sku-page {
  padding: 20px;
}

/* 物品选择器 */
.product-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

.selector-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.shop-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  margin-right: 12px;
}

.back-link {
  font-size: 14px;
  color: #3498db;
  text-decoration: none;
  margin-right: 12px;
  white-space: nowrap;
}

.back-link:hover {
  text-decoration: underline;
}

.selector-input {
  flex: 1;
  max-width: 400px;
}

.product-select {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
}

.sku-count-tip {
  font-size: 13px;
  color: #888;
  margin-left: auto;
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.search-box {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 280px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.add-btn {
  padding: 10px 20px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

/* 表格 */
.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: auto;
}

.sku-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1200px;
}

.sku-table th,
.sku-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.sku-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.sku-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}

.no-image {
  color: #999;
  font-size: 12px;
}

.empty-row {
  text-align: center;
  color: #999;
  padding: 40px 14px !important;
}

/* 标签 */
.default-tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: #f0f0f0;
  color: #888;
}

.default-tag.is-default {
  background: #fff3cd;
  color: #856404;
}

.status-tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.status-1 {
  background: #e8f5e9;
  color: #2ecc71;
}

.status-0 {
  background: #ffebee;
  color: #e74c3c;
}

/* 操作按钮 */
.action-btn {
  padding: 5px 10px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.view {
  background: #9b59b6;
  color: #fff;
}

.action-btn.edit {
  background: #3498db;
  color: #fff;
}

.action-btn.disable {
  background: #e74c3c;
  color: #fff;
}

.action-btn.enable {
  background: #2ecc71;
  color: #fff;
}

.action-btn.delete {
  background: #95a5a6;
  color: #fff;
}

/* 空状态 */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  color: #999;
  font-size: 15px;
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 560px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group-half {
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

/* 配置商品规格按钮 */
.config-btn {
  padding: 10px 18px;
  background: #f39c12;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-right: 10px;
}

.config-tip {
  margin: 0 0 14px;
  font-size: 13px;
  color: #888;
  line-height: 1.6;
}

.spec-check-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  max-height: 50vh;
  overflow: auto;
}

.spec-check-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}

.config-empty {
  color: #999;
  font-size: 13px;
}
</style>
